import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextareaAutosize,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Info, InfoOutlined } from "@mui/icons-material";
import { Controller, FormProvider, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import {
  Loader,
  TooltipLabel,
  DropdownV1,
  Clipboard,
  ButtonV1,
  ModalBox,
} from "components";
import { GridSkeleton } from "CustomStyledComponents";
import PaymentChip from "./PaymentChip";
import SellerTrack from "./SellerTrack";
import HoverBlock from "./HoverBlock";
import GridTable from "./GridTable";
import PaymentSummary from "./PaymentSummary";
import MultiSellerPaymentSummary from "./MultiSellerPaymentSummary";
import Discount from "./Discount";
import FileUpload from "./FileUpload";
import {
  ViewableItem,
  LineSeperator,
  RowSeparator,
  ColumnView,
} from "./CustomDetailComponents";
import BlockedAmountTable from "./BlockedAmountTable";
import NotificationStatusChip from "./components/NotificationStatusChip";
import ApprovedCreditLimitsModal from "./components/ApprovedCreditLimitsModal";
import PaymentChangeRequestModal from "./components/PaymentChangeRequestModal";

import theme from "themeProvider";

import {
  getTooltipDetails,
  getFormattedDiscount,
  getCashbackData,
  getMultiSellerSummary,
  getAmountsData,
} from "./orderDetailsServiceCall";
import {
  isValidQuantityForCreated,
  isValidQuantityForDelivery,
  getUpdatedData,
  makeCashbackData,
  getEmailStatus,
  getCommittedDeliveryDisplayValue,
  getCommittedDeliveryColor,
} from "./makeData";
import {
  setOrderDetailsAction,
  setCreditDetailsAction,
  setCustomerFreezeFlagAction,
} from "dataStore/orderDetails";
import {
  appendRupeeIcon,
  formatDate,
  moneyFormat,
  pascalCase,
  timeConvertor,
  validateNull,
  fetchTimeWithoutConversion,
} from "utils";
import { useFetchData, useToaster, useCategoryTypeCheck } from "hooks";
import { getCall_v2, postCall_v2, putCall_v2 } from "services";

import {
  seller,
  customer,
  DetailsTab,
  // Confirmed,
  emailStatusChip,
} from "./constants";
import {
  opsSpocList,
  success,
  error,
  reasonTypes,
  // JOMS_API_BASE_URL,
  STATUS_CHIP,
} from "constants";
import { OrderDetails_constant } from "../constants";
import {
  ORDER_PAYMENT_TRANSACTION_TYPE,
  ORDER_TRANSACTION_TYPES,
} from "pages/OrderList/constant";

const { creditFieldsLabel, CREDIT, JODL_ORDER_STATUS } = DetailsTab;

const {
  CREDIT_TYPE,
  CAM_ID,
  CASH_AMOUNT,
  CREDIT_PERIOD,
  PENALTY_CHARGE,
  AVAILABLE_LIMIT,
  EXPIRY_DATE,
  CREDIT_AMOUNT,
  CREDIT_BYPASS,
} = creditFieldsLabel;

const { OrderStatus } = OrderDetails_constant;

const OrderPaymentType = ({ orderInfo }) => {
  return (
    <ViewableItem
      label={"Order payment type"}
      value={validateNull(
        ORDER_TRANSACTION_TYPES[orderInfo?.orderPaymentTransactionType],
      )}
    />
  );
};

const OrderSummary = forwardRef(
  (
    {
      id,
      isOrderDetailsonMount,
      setIsInValidCashback,
      confirmationEmailStatus,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const triggerToaster = useToaster();

    const methods = useFormContext();

    const { getValues, control, watch, reset, resetField } = methods;

    const [orderInfo, setOrderInfo] = useState("");
    const [formattedAmounts, setFormattedAmounts] = useState(null);
    const [recordedTransaction, setRecordedTransaction] = useState(null);
    const [multiSellerSummary, setMultiSellerSummary] = useState(false);
    const [creditData, setCreditData] = useState(null);

    const [customerCashback, setCustomerCashback] = useState(null);
    const [sellerCashback, setSellerCashback] = useState(null);
    const [discounts, setDiscounts] = useState(null);
    const [openBlockedAmountModal, setOpenBlockedAmountModal] = useState(false);
    const [customerCreditInfo, setCustomerCreditInfo] = useState([]);
    const [selectedBlockedAmounts, setSelectedBlockedAmounts] = useState([]);
    const [eventStates, setEventStates] = useState();
    const [paymentOptionsData, setPaymentOptionsData] = useState(null);
    const [isLoadingPaymentOptions, setIsLoadingPaymentOptions] =
      useState(false);
    const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
    const [isApprovedCreditsModalOpen, setIsApprovedCreditsModalOpen] =
      useState(false);

    const orderDetails = useSelector((state) => state.orderDetails);
    const { isDirect } = useCategoryTypeCheck();

    const setCashBackResponse = (orderInfo) => {
      const { sellerCashbackObject, customerCashbackObject } =
        getCashbackData(orderInfo);
      setSellerCashback(sellerCashbackObject);
      setCustomerCashback(customerCashbackObject);
    };

    /*
     * @description - order details fetch call
     */
    const { refetch: fetchOrderDetails, isFetching } = useFetchData(
      "order-details",
      `/oms/order/v2/number/${id}`,
      (res) => {
        const { data } = res;
        data.invoiceDateThreshold = 150;
        setOrderInfo(data);
        recordedTransactions();

        setDiscounts(getFormattedDiscount(data));
        setCashBackResponse(data);
      },
    );

    /**
     *  @description - transaction/payments data call
     */
    const { refetch: recordedTransactions } = useFetchData(
      "recorder-transaction",
      `/oms/payment/recordedTransaction?customerOrderNumber=${id}`,
      (res) => {
        const { data } = res;
        setRecordedTransaction(data);
        //render lineitems
        isOrderDetailsonMount();

        if (orderInfo?.isMultiSeller) {
          const summaryAmount = getMultiSellerSummary(orderInfo);
          setMultiSellerSummary(summaryAmount);
          setFormattedAmounts(summaryAmount?.formattedAmounts);
        } else {
          const orderSummary = getAmountsData(orderInfo, data);
          setFormattedAmounts(orderSummary);
        }
      },
    );

    // Fetch reasons for cancellations
    const { refetch: fetchReasons, data: reasonList } = useFetchData(
      "fetch-cancel-reason-list",
      `oms/rejection/get-reasons-by-type?type=${reasonTypes?.ORDER_CANCELLATION}`,
    );

    /**
     * @description - business unit list - master data
     */
    const { data: businessUnitList, refetch: fetchBusinessList } = useFetchData(
      "business-unit",
      "/oms/master-data/BUSINESS_UNIT",
    );

    /**
     *  @description - order type list - master data
     */
    const { refetch: fetchOrderType } = useFetchData(
      "order-type",
      "/oms/master-data/ORDER_TYPE",
    );

    //  @description - fetch order credit details
    const getOrderCreditDetails = async () => {
      try {
        const { data } = await getCall_v2(
          `/joms/api/external/v1/credit-details?orderNumber=${id}`,
        );
        setCreditData(data);
        //publish details to redux
        const details = {
          isCreditBlocked: data?.isCreditBlocked,
          creditBlockedReason: data?.creditBlockedReason,
        };

        dispatch(setCreditDetailsAction(details));
      } catch (ex) {
        console.error("Exception in fetching credit order details");
      }
    };

    //  @description - fetch credit limit details
    const getCreditLimitsForCustomer = async (orderInfo) => {
      try {
        const { data } = await getCall_v2(
          `/joms/api/external/v2/getCreditInfoByPan?pan=${orderInfo?.buyerDetails?.gstin.substr(
            2,
            10,
          )}&gstin=${orderInfo?.buyerDetails?.gstin}`,
        );

        if (data?.customerCreditInfoSROS) {
          setCustomerCreditInfo(data.customerCreditInfoSROS);
        }

        const isNodal =
          orderInfo?.orderPaymentTransactionType ===
          ORDER_PAYMENT_TRANSACTION_TYPE.NODAL;

        const isCustomerCreditLimitBlocked = isNodal
          ? data?.isNodalCustomerCreditLimitBlocked ?? false
          : data?.isNonNodalCustomerCreditLimitBlocked ?? false;

        const blockedReason = isNodal
          ? data?.nodalBlockedReason ?? ""
          : data?.nonNodalBlockedReason ?? "";

        //publish details to redux
        const isCustomerFreezed = {
          customerFreezed: isCustomerCreditLimitBlocked,
          freezeReason: blockedReason,
        };
        dispatch(setCustomerFreezeFlagAction(isCustomerFreezed));
      } catch (ex) {
        console.error("Exception in fetching credit order details");
      }
    };

    /**
     *  @description - get blocked amount
     */
    const {
      data: blockedAmountInfo,
      refetch: fetchBlockedAmount,
      isFetching: isBlockedAmountFetching,
    } = useFetchData(
      "blocked-amount",
      `oms/credit/getBlockedAmountInfo?id=${creditData?.id}`,
    );

    const { data: eventStatus, refetch: fetchEventStatus } = useFetchData(
      "event-status",
      `/joms/api/external/v1/notification/fetch-event-status?reference=${id}`,
    );

    useEffect(() => {
      fetchBusinessList();
      fetchOrderType();
      fetchOrderDetails();
      fetchReasons();
      fetchEventStatus();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (eventStatus?.data?.data) {
        const transformedData = eventStatus?.data?.data.reduce(
          (acc, { notificationType, notificationStatus, message }) => {
            acc[notificationType] = {
              status: notificationStatus,
              chipColor: getChipColor(notificationStatus),
              message,
            };
            return acc;
          },
          {},
        );
        setEventStates(transformedData);
      }
    }, [eventStatus]);

    useEffect(() => {
      if (orderInfo?.jodlFailedReason) {
        const toasterType =
          orderInfo?.jodlOrderStatus === JODL_ORDER_STATUS.failed
            ? error
            : success;
        triggerToaster(orderInfo?.jodlFailedReason, toasterType);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderInfo]);

    useEffect(() => {
      (async () => {
        /* to dispatch redux action with payment data */
        if (orderInfo && recordedTransaction) {
          const customizedOrderData = {
            ...orderInfo,
            formattedAmounts: formattedAmounts,
            isDoFlowDisabled: orderInfo?.isDoFlowDisabled ?? false,
          };
          dispatch(setOrderDetailsAction(customizedOrderData));
          //invoke order credit details
          if (orderInfo?.paymentType === CREDIT) {
            await getOrderCreditDetails();
          }
          await getCreditLimitsForCustomer(orderInfo);
        }
      })();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderInfo, formattedAmounts]);

    useImperativeHandle(ref, () => ({
      /*
       * @description - order update function exposed
       */
      async onOrderUpdate(isPayoutApplicable) {
        let isShipmentAlreadyCreated = false;
        Object.values(orderDetails).forEach((lineItem) => {
          if (lineItem?.lineItemShipments?.[0]?.shipmentId) {
            isShipmentAlreadyCreated = true;
          }
        });

        const updateData = getUpdatedData(
          getValues(),
          id,
          orderInfo,
          isPayoutApplicable,
        );
        const { amounts, orderStatus } = orderInfo;
        switch (orderStatus) {
          case "Created": {
            if (updateData?.orderStatus === OrderStatus?.placed) {
              if (isValidQuantityForCreated(amounts)) {
                await updateOrderDetails(updateData);
              } else {
                triggerToaster(`Payment for this order is not complete`, error);
              }
            }
            break;
          }
          case "Placed": {
            if (updateData?.orderStatus === OrderStatus.inProgress) {
              if (isShipmentAlreadyCreated) {
                await updateOrderDetails(updateData);
              } else {
                triggerToaster(
                  `Please create a shipment for this order`,
                  error,
                );
              }
            } else {
              await updateOrderDetails(updateData);
            }
            break;
          }
          case "Delivery in progress":
          case "Partially Delivered": {
            if (updateData?.orderStatus === OrderStatus.delivered) {
              const { lineItemDetails } = orderDetails;
              if (isValidQuantityForDelivery(lineItemDetails)) {
                await updateOrderDetails(updateData);
              } else {
                triggerToaster(
                  `Delivered Quantity + Short closed + Cancelled should be greater than equal to 95% of Ordered quantity for all line items`,
                  error,
                );
              }
            } else {
              await updateOrderDetails(updateData);
            }
            break;
          }
          default: {
            await updateOrderDetails(updateData);
            break;
          }
        }
        if (getValues("orderStatus") !== OrderStatus.cancelled) {
          const cashbackData = makeCashbackData(orderInfo, getValues());
          await postCall_v2(
            `/oms/order/cashback-update?customerOrderId=${orderInfo.id}`,
            cashbackData,
            "",
          );
        }
      },
      fetchOrderDetails: () => fetchOrderDetails(),
      getCreditData: () => creditData,
      fetchUpdatedValues: () => getValues(),
      resetField: (key) => resetField(key),
    }));

    const navigateToOrderList = useCallback(
      () => navigate(`/order-list/${id}?tab=orderDetails`),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [navigate],
    );

    const updateOrderDetails = async (orderDetails) => {
      try {
        await putCall_v2(
          "/joms/api/external/v1/order/order-update",
          orderDetails,
        );
        reset();
        triggerToaster(`Order ${id} updated successfully`, success);
        navigateToOrderList();
        fetchOrderDetails();
        fetchEventStatus();
      } catch (err) {
        triggerToaster(err?.data?.errorMessage, error);
      }
    };

    const isCreditOrder = orderInfo?.paymentType === CREDIT;
    const { amounts, multiSellerOrderAmount = null } = orderInfo;

    const fetchPaymentInstrumentOptions = async () => {
      try {
        setIsLoadingPaymentOptions(true);
        const res = await getCall_v2(
          `/joms/api/external/v1/customer-order/${id}/payment-instrument/options`,
        );
        setPaymentOptionsData(res?.data);
      } catch (err) {
        triggerToaster(
          err?.data?.message || "Failed to fetch payment options",
          error,
        );
      } finally {
        setIsLoadingPaymentOptions(false);
      }
    };

    const handleOpenChangeModal = () => {
      setPaymentOptionsData(null);
      fetchPaymentInstrumentOptions();
      setIsChangeModalOpen(true);
    };

    const handleCloseChangeModal = useCallback(() => {
      setIsChangeModalOpen(false);
      setPaymentOptionsData(null);
    }, []);

    const handleSubmitChangeRequest = useCallback(
      async (selectedOption) => {
        try {
          const payload = {
            creditLimitId: selectedOption?.creditLimitId,
            reason: "",
          };

          await postCall_v2(
            `/joms/api/external/v1/customer-order/${id}/payment-instrument/switch-requests`,
            payload,
          );

          triggerToaster(
            "Payment change request submitted successfully",
            success,
          );
          handleCloseChangeModal();
        } catch (err) {
          triggerToaster(
            err?.data?.errorMessage || "Failed to submit change request",
            error,
          );
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [id, handleCloseChangeModal],
    );

    const ADP_amount = multiSellerOrderAmount
      ? Object.values(multiSellerOrderAmount)[0]?.["ADP"]
      : amounts?.["ADP"];
    const cashAmount = ADP_amount?.amount?.centAmount / 100;

    const handleShowBreakUp = useCallback(
      async (item) => {
        if (item) {
          const targetId = item.id;
          if (targetId) {
            try {
              const res = await getCall_v2(
                `/oms/credit/getBlockedAmountInfo?id=${targetId}`,
              );
              if (res?.data) {
                setSelectedBlockedAmounts(res.data);
                setOpenBlockedAmountModal(true);
                return;
              }
            } catch (err) {
              console.warn(
                "GetBlockedAmountInfo API failed, using item blockedDetails",
                err,
              );
            }
          }
        }

        // Default breakup from order credit details
        if (creditData?.id) {
          fetchBlockedAmount();
        } else {
          setSelectedBlockedAmounts(blockedAmountInfo?.data || []);
        }
        setOpenBlockedAmountModal(true);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [creditData?.id, fetchBlockedAmount, blockedAmountInfo?.data],
    );

    const getChipColor = (status) => {
      switch (status) {
        case emailStatusChip.INITIATED:
          return STATUS_CHIP.warning;
        case emailStatusChip.SUCCESS:
          return STATUS_CHIP.success;
        case emailStatusChip.FAILED:
          return STATUS_CHIP.error;
        default:
          return STATUS_CHIP.success;
      }
    };

    const { declined, cancelled, placed, inProgress, created } = OrderStatus;
    const isOrderCancelledOrDeclined =
      [declined, cancelled].includes(watch("orderStatus")) ||
      orderInfo?.orderStatus === cancelled;

    const allowedChangePaymentStatuses = [
      placed,
      "Confirmed",
      inProgress,
      created,
    ];
    const canInitiatePaymentChange = allowedChangePaymentStatuses.includes(
      orderInfo?.orderStatus,
    );

    if (isFetching) return <Loader />;

    if (!orderInfo)
      return (
        <Typography
          variant="h5"
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          Something went wrong. Try after sometime.
        </Typography>
      );

    const gstVerifiedDateTime = moment(orderInfo.gstVerifiedDateTime).format(
      "DD-MM-YYYY hh:mm:ssA",
    );

    const isToolTipAvailable =
      orderInfo.gstDetailsVerifiedBy || orderInfo.gstVerifiedDateTime;

    const statusData = confirmationEmailStatus?.data?.data;

    const buyerEmailData = getEmailStatus(
      statusData,
      "ORDER_CONFIRMED_CUSTOMER",
      orderInfo?.buyerDetails?.email,
    );

    const bnplBuyerEmailData = getEmailStatus(
      statusData,
      "BNPL_ORDER_CONFIRMED_CUSTOMER",
      orderInfo?.buyerDetails?.email,
    );

    const buyerEmailStatus = buyerEmailData?.status
      ? buyerEmailData
      : bnplBuyerEmailData;

    return (
      <>
        <FormProvider {...methods}>
          <Box display={"flex"} mb={12} as="form">
            <Box style={{ width: "75%" }} pl={4} pr={4}>
              <Box>
                {/* first row */}
                <RowSeparator>
                  <GridSkeleton
                    firstChild={
                      <ViewableItem
                        label={"Buyer order no."}
                        value={orderInfo?.orderNumber}
                      />
                    }
                    secondChild={
                      <ViewableItem
                        label={"Order date"}
                        value={`${formatDate(
                          orderInfo?.orderDate,
                        )}, ${timeConvertor(orderInfo?.orderDate + "Z")}`}
                      />
                    }
                    thirdChild={
                      <ColumnView label={"Buyer"}>
                        <TooltipLabel
                          placement="right"
                          title={
                            <HoverBlock data={getTooltipDetails(orderInfo)} />
                          }
                          labelChildren={
                            <Typography
                              fontSize={16}
                              fontWeight={"bold"}
                              color={theme.palette.secondary.main}
                              style={{ wordBreak: "break-word" }}
                            >
                              {pascalCase(orderInfo?.buyerDetails?.name)}
                            </Typography>
                          }
                        />
                      </ColumnView>
                    }
                  />
                </RowSeparator>
                {/* second row */}
                <RowSeparator>
                  <Box display={"flex"} flexDirection={"column"}>
                    <Box>
                      <GridSkeleton
                        firstChild={
                          <ColumnView label={"Payment status"}>
                            <PaymentChip
                              paymentStatus={orderInfo?.paymentStatus}
                              paymentCompletionDate={
                                orderInfo?.paymentCompletionDate
                              }
                            />
                          </ColumnView>
                        }
                        secondChild={
                          <ViewableItem
                            label={"Payment type"}
                            value={validateNull(orderInfo?.paymentType)}
                          />
                        }
                        thirdChild={
                          <Box>
                            <GridSkeleton
                              firstChild={
                                <ViewableItem
                                  label={"Order category"}
                                  value={orderInfo?.category}
                                />
                              }
                            />
                          </Box>
                        }
                      />
                    </Box>
                  </Box>
                </RowSeparator>
                <RowSeparator>
                  <>
                    <GridSkeleton
                      firstChild={
                        isCreditOrder && (
                          <Box
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "baseline",
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={0.75}
                            >
                              <Typography style={{ marginBottom: 0 }}>
                                {CREDIT_TYPE} |
                              </Typography>
                              <Typography
                                sx={{
                                  color: theme.palette.primary.main,
                                  cursor: "pointer",
                                  fontSize: 15,
                                  fontWeight: 600,
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsApprovedCreditsModalOpen(true);
                                }}
                              >
                                View approved credits
                              </Typography>
                            </Box>
                            <Typography
                              style={{
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                              }}
                            >
                              {validateNull(
                                `${creditData?.creditType ?? ""} - ${
                                  creditData?.creditInstrument ?? ""
                                }`.trim(),
                              )}
                            </Typography>
                          </Box>
                        )
                      }
                      secondChild={
                        isCreditOrder && (
                          <ViewableItem
                            label={CAM_ID}
                            value={validateNull(
                              `${creditData?.creditLimitRefId ?? ""}` ?? "",
                            )}
                          />
                        )
                      }
                      thirdChild={
                        isCreditOrder && (
                          <ViewableItem
                            label={CREDIT_BYPASS}
                            value={validateNull(
                              `${orderDetails?.creditBlockBypassFlag ?? ""}` ??
                                "",
                            )}
                          />
                        )
                      }
                    />
                  </>
                </RowSeparator>
                <RowSeparator>
                  {!!orderInfo?.files &&
                    !!Object.keys(orderInfo?.files)?.length && (
                      <Box>
                        <Typography>Attachment</Typography>
                        <Grid container>
                          {Object.keys(orderInfo?.files).map((type) => (
                            <Grid item xs={4}>
                              <FileUpload
                                fileDetails={orderInfo?.files[type]}
                                type={type}
                                orderNumber={orderInfo?.orderNumber}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                </RowSeparator>
                {/*Second row(A) for Credit payment type */}
                {isCreditOrder && (
                  <RowSeparator>
                    <Box display={"flex"} flexDirection={"column"}>
                      <GridSkeleton
                        firstChild={
                          <ViewableItem
                            label={CASH_AMOUNT}
                            value={moneyFormat(cashAmount)}
                          />
                        }
                        secondChild={
                          <ViewableItem
                            label={CREDIT_PERIOD}
                            value={creditData?.creditDueDay}
                          />
                        }
                        thirdChild={
                          <ViewableItem
                            label={PENALTY_CHARGE}
                            value={validateNull(creditData?.penalRate)}
                          />
                        }
                      />
                    </Box>
                  </RowSeparator>
                )}
                {/*Second row(B) for Credit payment type */}
                {isCreditOrder && (
                  <RowSeparator>
                    <Box display={"flex"} flexDirection={"column"}>
                      <GridSkeleton
                        firstChild={
                          <Box
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "baseline",
                            }}
                          >
                            <ViewableItem
                              label={AVAILABLE_LIMIT}
                              value={moneyFormat(creditData?.creditUsable)}
                            />
                            <ButtonV1
                              onClick={handleShowBreakUp}
                              title="Show breakup"
                              variant="text"
                              disableRipple={true}
                              loading={isBlockedAmountFetching}
                              endIcon={
                                <img
                                  width={16}
                                  src="/assets/external-link.svg"
                                  style={{
                                    stroke: theme.palette.primary.main,
                                  }}
                                  alt="external link icon"
                                />
                              }
                              style={{
                                fontSize: 12,
                                padding: 5,
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        }
                        secondChild={
                          <ViewableItem
                            label={EXPIRY_DATE}
                            value={formatDate(creditData?.creditExpiry)}
                          />
                        }
                        thirdChild={
                          <ViewableItem
                            label={CREDIT_AMOUNT}
                            value={moneyFormat(creditData?.creditAmount ?? 0)}
                          />
                        }
                      />
                    </Box>
                  </RowSeparator>
                )}
                <LineSeperator />
                <RowSeparator>
                  <GridSkeleton
                    firstChild={
                      !!orderInfo?.salesforceOpportunityId && (
                        <ColumnView label={"Opportunity no."}>
                          <Box style={{ display: "flex" }}>
                            <Button
                              href={orderInfo?.salesforceOpportunityUrl}
                              target="_blank"
                              style={{
                                padding: 0,
                                height: "fit-content",
                              }}
                            >
                              {orderInfo?.salesforceOpportunityId}
                              <br />
                              {orderInfo?.opportunityString &&
                                `(${orderInfo?.opportunityString})`}
                            </Button>
                            <Clipboard
                              text={orderInfo?.salesforceOpportunityId}
                              message="Opportunity no. copied"
                              style={{ marginTop: -4 }}
                            />
                          </Box>
                        </ColumnView>
                      )
                    }
                    secondChild={""}
                    thirdChild={
                      <Box display="flex" alignItems="center">
                        <Controller
                          control={control}
                          name={"shippingAddressVerified"}
                          defaultValue={orderInfo?.shippingAddressVerified}
                          render={({ field: { value, onChange } }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  value={value}
                                  checked={!!value}
                                  onChange={onChange}
                                  disableRipple
                                />
                              }
                              label={
                                <Box display="flex" alignItems="center">
                                  <Typography variant="body2">
                                    Shipping address verified
                                  </Typography>
                                </Box>
                              }
                            />
                          )}
                        />
                        {!!orderInfo?.declarationLink && (
                          <Button
                            href={orderInfo?.declarationLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ padding: 0, minWidth: "auto" }}
                          >
                            Declaration
                          </Button>
                        )}
                      </Box>
                    }
                  />
                </RowSeparator>
                <RowSeparator>
                  <GridSkeleton
                    firstChild={
                      <Controller
                        control={control}
                        name={"isVerifiedPO"}
                        defaultValue={orderInfo?.isVerifiedPO}
                        render={({ field: { value, onChange } }) => (
                          <FormControlLabel
                            label={"Verify PI/PO"}
                            control={
                              <Checkbox
                                disabled={orderInfo?.isVerifiedPO}
                                value={value}
                                checked={!!value}
                                onChange={onChange}
                                disableRipple
                              />
                            }
                          />
                        )}
                      />
                    }
                    secondChild={
                      !!isDirect && (
                        <Controller
                          control={control}
                          name={"isVerifiedSO"}
                          defaultValue={orderInfo?.isVerifiedSO}
                          render={({ field: { value, onChange } }) => (
                            <FormControlLabel
                              label={"Verify SO"}
                              control={
                                <Checkbox
                                  disabled={orderInfo?.isVerifiedSO}
                                  value={value}
                                  checked={!!value}
                                  onChange={onChange}
                                  disableRipple
                                />
                              }
                            />
                          )}
                        />
                      )
                    }
                    thirdChild={
                      <Box display="flex" alignItems="center">
                        <Controller
                          control={control}
                          name={"isGstDetailsVerified"}
                          defaultValue={orderInfo?.gstDetailsVerified}
                          render={({ field: { value, onChange } }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  value={value}
                                  checked={!!value}
                                  onChange={onChange}
                                  disableRipple
                                />
                              }
                              label={
                                <Box display="flex" alignItems="center">
                                  <Typography variant="body2">
                                    Billing address verified
                                  </Typography>
                                  {!!isToolTipAvailable && (
                                    <TooltipLabel
                                      customStyle={{ boxShadow: "none" }}
                                      placement="bottom"
                                      title={
                                        <Box
                                          sx={{
                                            backgroundColor:
                                              theme.palette.grey["400"],
                                            padding: "8px 12px",
                                            borderRadius: "4px",
                                            display: "inline-block",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color="textPrimary"
                                          >
                                            Modified by:{" "}
                                            {orderInfo?.orderConfirmedBy}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="textPrimary"
                                          >
                                            at {gstVerifiedDateTime}
                                          </Typography>
                                        </Box>
                                      }
                                      labelChildren={
                                        <Info
                                          style={{
                                            width: 16,
                                            marginLeft: 4, // You can reduce/remove this too
                                          }}
                                          color={"primary"}
                                        />
                                      }
                                    />
                                  )}
                                </Box>
                              }
                            />
                          )}
                        />
                      </Box>
                    }
                  />
                </RowSeparator>
                {/* third row */}
                <RowSeparator>
                  <GridSkeleton
                    firstChild={
                      <ViewableItem
                        label={"Business unit"}
                        value={orderInfo?.businessUnit}
                      />
                    }
                    secondChild={
                      <ViewableItem
                        label={"Mode of order"}
                        value={orderInfo?.mode}
                      />
                    }
                    thirdChild={
                      <ViewableItem
                        label={"Delivery type"}
                        value={orderInfo?.deliveryType ?? "-"}
                      />
                    }
                  />
                </RowSeparator>
                {/* fourth row */}
                <RowSeparator>
                  <GridSkeleton
                    firstChild={
                      <ViewableItem
                        label={"Order status"}
                        value={orderInfo?.orderStatus}
                      />
                      // <ColumnView label={"Order status"}>
                      //   <Controller
                      //     control={control}
                      //     defaultValue={orderInfo?.orderStatus || ""}
                      //     name={"orderStatus"}
                      //     render={({ field: { value, onChange } }) => (
                      //       <DropdownV1
                      //         onChange={onChange}
                      //         itemList={orderInfo?.nextOrderStates.filter(
                      //           (ele) => ele.state !== Confirmed,
                      //         )}
                      //         valueKey="state"
                      //         value={value}
                      //         menuStyle={{ minWidth: 170 }}
                      //       />
                      //     )}
                      //   />
                      // </ColumnView>
                    }
                    secondChild={
                      <ViewableItem
                        label={"Order confirmation date"}
                        value={
                          orderInfo?.orderConfirmationDate
                            ? `${formatDate(
                                orderInfo?.orderConfirmationDate,
                              )}, ${fetchTimeWithoutConversion(
                                orderInfo?.orderConfirmationDate,
                              )}`
                            : "-"
                        }
                      />
                    }
                    thirdChild={

                      <ViewableItem
                        label={"Committed Delivery Date"}
                        value={
                          <Typography
                            sx={{
                              color: getCommittedDeliveryColor(
                                orderInfo?.orderStatus,
                                orderInfo?.committedDeliveryDate,
                              ),
                              fontWeight: 600,
                            }}
                          >
                            {getCommittedDeliveryDisplayValue(
                              orderInfo?.orderStatus,
                              orderInfo?.committedDeliveryDays,
                              orderInfo?.committedDeliveryDate,
                            )}
                          </Typography>
                        }
                      />
                    }
                  />
                </RowSeparator>
                <GridSkeleton

                firstChild={
                    <ViewableItem
                      label={"Order confirmed by"}
                      value={
                        orderInfo?.orderConfirmedBy
                          ? orderInfo.orderConfirmedBy
                          : "-"
                      }
                    />
                  }
                  secondChild={
                    <>
                      {!!(
                        orderInfo?.orderStatus === OrderStatus.cancelled ||
                        watch("orderStatus") === OrderStatus.cancelled
                      ) && (
                        <ColumnView
                          label={"Reason for Cancellation / Rejection"}
                        >
                          <Controller
                            control={control}
                            defaultValue={orderInfo?.reasonForCancellation}
                            name={"reasonForCancellation"}
                            render={({ field: { value, onChange } }) => (
                              <DropdownV1
                                onChange={onChange}
                                itemList={reasonList?.data}
                                displayKey="reason"
                                valueKey={"reason"}
                                value={value}
                                menuStyle={{ minWidth: 170, maxWidth: 170 }}
                              />
                            )}
                          />
                        </ColumnView>
                      )}
                      {watch("orderStatus") === OrderStatus.declined && (
                        <ColumnView label={"Reason for decline"}>
                          <Controller
                            control={control}
                            defaultValue={orderInfo?.reasonForDecline}
                            name={"reasonForDecline"}
                            render={({ field: { value, onChange } }) => (
                              <TextareaAutosize
                                value={value}
                                onChange={onChange}
                                minRows={1}
                                style={{
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  resize: "none",
                                  padding: 10,
                                  borderRadius: 8,
                                  minWidth: 220,
                                }}
                              />
                            )}
                          />
                        </ColumnView>
                      )}
                      {!isOrderCancelledOrDeclined && (
                        <OrderPaymentType orderInfo={orderInfo} />
                      )}
                    </>
                  }
                  thirdChild={
                    isOrderCancelledOrDeclined && (
                      <OrderPaymentType orderInfo={orderInfo} />
                    )
                  }
                />
                <RowSeparator></RowSeparator>
                <LineSeperator />
                {/* fifth row */}
                <RowSeparator>
                  <SellerTrack
                    orderInfo={orderInfo}
                    businessUnitList={businessUnitList}
                  />
                </RowSeparator>
                <LineSeperator />
                {/* sixth row */}
                <RowSeparator>
                  <GridSkeleton
                    firstChild={
                      <ColumnView label={"Ops SPOC"}>
                        <Controller
                          control={control}
                          defaultValue={orderInfo?.opsSpoc}
                          name={"opsSpoc"}
                          render={({ field: { value, onChange } }) => (
                            <DropdownV1
                              onChange={onChange}
                              itemList={opsSpocList}
                              value={value}
                              menuStyle={{ minWidth: 170 }}
                            />
                          )}
                        />
                      </ColumnView>
                    }
                    secondChild={
                      <>
                        {buyerEmailStatus?.status && (
                          <ColumnView label={"Buyer email"}>
                            <NotificationStatusChip
                              label={pascalCase(buyerEmailStatus?.status)}
                            />
                            {buyerEmailStatus?.failure_reason && (
                              <TooltipLabel
                                title={buyerEmailStatus?.failure_reason}
                                labelChildren={
                                  <InfoOutlined
                                    fontSize="small"
                                    style={{
                                      verticalAlign: "middle",
                                      color: theme.palette.error.dark,
                                    }}
                                  />
                                }
                              />
                            )}
                          </ColumnView>
                        )}
                      </>
                    }
                  />
                </RowSeparator>
              </Box>
            </Box>
            <Box style={{ flex: 1 }}>
              {formattedAmounts && !multiSellerSummary && (
                <PaymentSummary data={formattedAmounts} />
              )}

              {canInitiatePaymentChange && (
                <Box sx={{ mb: 4 }}>
                  <ButtonV1
                    title="Initiate change payment request"
                    onClick={handleOpenChangeModal}
                  />
                </Box>
              )}

              {multiSellerSummary && (
                <Box mb={8}>
                  <MultiSellerPaymentSummary
                    title={"Seller payment summary"}
                    orderSummary={multiSellerSummary}
                  />
                </Box>
              )}

              {!!discounts && <Discount data={discounts} />}

              {sellerCashback && (
                <Box mb={8} style={{ display: "none" }}>
                  <GridTable
                    data={sellerCashback}
                    title="Seller cashbacks"
                    type={seller}
                    verifyCashback={setIsInValidCashback}
                  />
                </Box>
              )}

              {customerCashback && (
                <Box mb={8}>
                  <GridTable
                    amounts={orderInfo?.amounts}
                    data={customerCashback}
                    title="Buyer cashbacks"
                    verifyCashback={setIsInValidCashback}
                    buyerCashback={
                      orderInfo?.buyerCashback
                        ? appendRupeeIcon(orderInfo?.buyerCashback)
                        : ""
                    }
                    type={customer}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <ModalBox
            open={openBlockedAmountModal}
            onCloseModal={() => setOpenBlockedAmountModal(false)}
            width={"60%"}
          >
            <BlockedAmountTable
              onClose={() => setOpenBlockedAmountModal(false)}
              data={selectedBlockedAmounts}
            />
          </ModalBox>
          <PaymentChangeRequestModal
            open={isChangeModalOpen}
            onClose={handleCloseChangeModal}
            data={paymentOptionsData}
            isLoading={isLoadingPaymentOptions}
            onSubmit={handleSubmitChangeRequest}
          />
          <ApprovedCreditLimitsModal
            open={isApprovedCreditsModalOpen}
            onClose={() => setIsApprovedCreditsModalOpen(false)}
            data={customerCreditInfo}
            onViewBlockedAmount={handleShowBreakUp}
          />
        </FormProvider>
      </>
    );
  },
);

export default OrderSummary;
